import React, { memo, useEffect, useCallback, useState } from 'react'
import PropTypes from 'prop-types'
import { CardWrapper } from './StyledPromiseToPayTable'

import { Icon, Card } from '@salesforce/design-system-react'

import { CardSearchHeader, LightningDataTable } from '../../common'

import getColumnDefinitions from './promiseToPayTableColumns'
import { filterRecords, generateTableColumns, sortRecords } from '../../../helpers/dataTable'

import { TABLE_HEIGHT_PROMISE2PAY } from '../../../constants'

const PromiseToPayTable = ({ data, onLoadMore, totalItems }) => {
  const [internalFilterItems, setInternalFilterItems] = useState([])
  const [itemSearch, setItemSearch] = useState('')
  const [searching, setSearching] = useState(false)
  const [currentSortColumn, setCurrentSortColumn] = useState(null)
  const [sortDirectionMemo, setSortDirectionMemo] = useState({})

  const isEmpty = data.length === 0

  const filterChangedHandler = useCallback(event => {
    setItemSearch(event.target.value)
  }, [])

  const onSortHandler = useCallback(
    sortColumn => {
      const { sortDirection, property: sortProperty } = sortColumn

      // This method may return null if data provided item is not array
      const sortedItems = sortRecords(
        internalFilterItems,
        sortProperty,
        sortDirection
      )

      if (sortedItems) {
        setInternalFilterItems(sortedItems)
        setCurrentSortColumn(sortProperty)
        setSortDirectionMemo(prev => ({
          ...prev,
          [sortProperty]: sortDirection
        }))
      }
    },
    [internalFilterItems]
  )

  const onSearchHandler = useCallback(() => {
    let filteredItems = filterRecords(itemSearch, data, columnDefinitions)

    if (currentSortColumn != null) {
      filteredItems = sortRecords(
        filteredItems,
        currentSortColumn,
        sortDirectionMemo
      )
    }
    setInternalFilterItems(filteredItems)
    setSearching(!!itemSearch)
  }, [itemSearch, currentSortColumn, sortDirectionMemo, data])

  useEffect(() => {
    let computedData = filterRecords(itemSearch, data, columnDefinitions)
    if (currentSortColumn != null) {
      computedData = sortRecords(
        computedData,
        currentSortColumn,
        sortDirectionMemo
      )
    }

    setInternalFilterItems(computedData)
    return () => {
      setInternalFilterItems([])
    }
  }, [data])

  const columnDefinitions = getColumnDefinitions({
    sortColumn: currentSortColumn,
    sortDirectionMemo
  })

  const columns = generateTableColumns(columnDefinitions)

  return (
    <div style={{ overflow: 'auto' }}>
      <CardWrapper>
        <Card
          id='ExampleCard'
          header={
            <CardSearchHeader
              headerText={`Pending Promise to Pays (${totalItems})`}
              icon={<Icon category='standard' name='work_capacity_usage' />}
              isEmpty={isEmpty}
              onFilterChange={filterChangedHandler}
              onSearch={onSearchHandler}
            />
          }
        >
          <LightningDataTable
            columns={columns}
            items={internalFilterItems}
            id='promiseToPayTable'
            onLoadMore={onLoadMore}
            hasMore={data.length < totalItems}
            onSort={onSortHandler}
            className={'no-shrink-column'}
            totalItems={totalItems}
            tableHeight={TABLE_HEIGHT_PROMISE2PAY}
            isFiltering={!!searching}
          />
        </Card>
      </CardWrapper>
    </div>
  )
}

PromiseToPayTable.propTypes = {
  data: PropTypes.array.isRequired,
  onLoadMore: PropTypes.func.isRequired,
  totalItems: PropTypes.number.isRequired
}

export default memo(PromiseToPayTable)
